import React, { forwardRef, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { isBrowser } from '../common/util.js';
import { usePrevious } from '../common/hooks.js';
import { MODAL_ACTION_OPEN, MODAL_ACTION_CLOSE } from './constants.js';
import { useModalState, useScrollLock } from './hooks.js';
import { ModalDialog } from './ModalDialog.js';
import { ModalContext } from './ModalContext.js';
import { ModalBlanket } from './ModalBlanket.js';
import { FocusTrapStyle } from './Modal.styles.js';

const defaultProps = {
    blanket: true,
    blanketProps: {},
    closeButton: true,
    closeOnOutsideClick: false,
    cover: false,
    divider: false,
    footer: null,
    header: null,
    open: false,
    position: 'center',
    rounding: 'sharp',
    sizing: 'medium',
    width: 'medium',
};
/**
 * This component is used to ensure that there is at least one tab stop outside
 * the modal dialog that triggers a focus event, if the user tries to move
 * the focus out from the dialog.
 *
 * Without the use of this component the focus may escape directly from
 * the document to some button in the browser user interface especially
 * in Windows broweers and there is no way to gain it back programmatically.
 */
const FocusTrapTabStop = styled.div.attrs(() => ({ tabIndex: 0 })) `
    ${FocusTrapStyle}
`;
const _Modal = forwardRef(({ blanket, blanketProps, closeOnOutsideClick, cover, onClose, onOpen, open: propOpen, portalParent, sizing, ...rest }, dialogRef) => {
    const leadingTrapRef = useRef(null);
    const [{ open }, dispatch] = useModalState({
        open: propOpen !== null && propOpen !== void 0 ? propOpen : defaultProps.open,
    });
    const previousOpen = usePrevious(open);
    useEffect(() => {
        if (typeof propOpen !== 'undefined') {
            dispatch(propOpen ? MODAL_ACTION_OPEN : MODAL_ACTION_CLOSE);
        }
    }, [propOpen]);
    useEffect(() => {
        if (open) {
            if (leadingTrapRef.current) {
                leadingTrapRef.current.focus();
            }
            if (typeof onOpen === 'function') {
                onOpen();
            }
        }
        if (previousOpen && !open && typeof onClose === 'function') {
            onClose();
        }
    }, [open]);
    useScrollLock(open);
    return isBrowser() && open
        ? createPortal(React.createElement(ModalContext.Provider, { value: { dispatch, sizing } },
            React.createElement(ModalBlanket, { ...blanketProps, blanket: blanket, closeButton: rest.closeButton, closeOnOutsideClick: closeOnOutsideClick, cover: cover },
                React.createElement(FocusTrapTabStop, { ref: leadingTrapRef }),
                React.createElement(ModalDialog, { cover: cover, sizing: sizing, ...rest, ref: dialogRef }),
                React.createElement(FocusTrapTabStop, null))), portalParent !== null && portalParent !== void 0 ? portalParent : document.body)
        : null;
});
_Modal.defaultProps = defaultProps;
_Modal.displayName = '_Modal';
const Modal = styled(_Modal) ``;

export { Modal, defaultProps };
//# sourceMappingURL=Modal.js.map
